// Client-side banner pipeline.
//
// Why client-side: a 5MB phone photo would blow past Workers' CPU budget if
// re-encoded server-side. The admin's browser has plenty of CPU and a real
// canvas, so we resize + re-encode there and ship pre-sized variants to R2.
//
// Outputs:
//   - jpeg @ 1600w  (canonical fallback / large screen)
//   - jpeg @ 960w
//   - jpeg @ 480w
//   - webp @ 1600w  (modern browsers, smaller)
//   - webp @ 960w
//   - webp @ 480w
//
// Also samples a coarse 4-bit-per-channel histogram over the 480w buffer
// and returns a theme suggestion (primary + accent) filtered by mode.

export type ThemeMode = "joyful" | "mourning";

export type ExtractedTheme = {
  primary: string; // hex
  accent: string; // hex
  swatches: string[]; // top 5 picks for the admin to override
  mode: ThemeMode;
};

export type EncodedVariant = {
  blob: Blob;
  filename: string;
  width: number;
  height: number;
  format: "jpeg" | "webp";
};

export type PipelineOutput = {
  id: string;
  alt: string;
  variants: EncodedVariant[];
  preview: string; // dataURL of the 480w jpeg for instant UI feedback
  theme: ExtractedTheme;
};

type EncodeTarget = {
  width: number;
  format: "jpeg" | "webp";
  quality: number;
};

const TARGETS: EncodeTarget[] = [
  { width: 480, format: "jpeg", quality: 0.82 },
  { width: 960, format: "jpeg", quality: 0.84 },
  { width: 1600, format: "jpeg", quality: 0.86 },
  { width: 480, format: "webp", quality: 0.8 },
  { width: 960, format: "webp", quality: 0.82 },
  { width: 1600, format: "webp", quality: 0.85 },
];

export async function processBanner(
  file: File,
  options: { alt: string; mode?: ThemeMode } = { alt: "" },
): Promise<PipelineOutput> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Pick an image file (JPEG, PNG, or WebP).");
  }
  if (file.size > 25 * 1024 * 1024) {
    throw new Error("Image is over 25 MB. Resize and try again.");
  }

  const source = await loadImage(file);
  const variants: EncodedVariant[] = [];
  const id = crypto.randomUUID();
  let previewBlob: Blob | null = null;

  for (const target of TARGETS) {
    const canvas = drawResized(source, target.width);
    const actualWidth = canvas.width; // may be smaller than target.width for small originals
    const actualHeight = canvas.height;
    const blob = await canvasToBlob(canvas, target.format, target.quality);
    variants.push({
      blob,
      filename: `banner-${actualWidth}.${target.format}`,
      width: actualWidth,
      height: actualHeight,
      format: target.format,
    });
    if (target.width === 480 && target.format === "jpeg") previewBlob = blob;
  }

  // Drop duplicates that collapsed to the same (width, format) when the
  // source was smaller than the target sizes.
  const seen = new Set<string>();
  const deduped = variants.filter((v) => {
    const key = `${v.width}/${v.format}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const previewSource = previewBlob ?? (await canvasToBlob(drawResized(source, 480), "jpeg", 0.78));
  const preview = await blobToDataURL(previewSource);

  // Theme extraction works off a small sampled buffer.
  const sampleCanvas = drawResized(source, 320);
  const theme = extractTheme(sampleCanvas, options.mode ?? "joyful");

  return {
    id,
    alt: options.alt,
    variants: deduped,
    preview,
    theme,
  };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  let settled = false;
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const finalize = (fn: () => void) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      fn();
    };
    img.onload = () => finalize(() => resolve(img));
    img.onerror = () =>
      finalize(() => reject(new Error("Could not decode image. Try JPEG or PNG.")));
    img.src = url;
    // Belt-and-suspenders: cap on stalled decoder.
    window.setTimeout(() => {
      if (!settled) finalize(() => reject(new Error("Decoding timed out.")));
    }, 30_000);
  });
}

function drawResized(img: HTMLImageElement, targetWidth: number): HTMLCanvasElement {
  const ratio = img.naturalHeight / img.naturalWidth;
  const width = Math.min(targetWidth, img.naturalWidth);
  const height = Math.round(width * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: targetWidth <= 480 });
  if (!ctx) throw new Error("Canvas unavailable.");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  format: "jpeg" | "webp",
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(`Encoding to ${format} failed.`))),
      `image/${format}`,
      quality,
    );
  });
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Preview failed."));
    reader.readAsDataURL(blob);
  });
}

// Coarse dominant-color extraction. Quantize each channel to 4 bits, build a
// histogram, score buckets by frequency × saturation, filter by lightness.
function extractTheme(canvas: HTMLCanvasElement, mode: ThemeMode): ExtractedTheme {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return fallbackTheme(mode);
  }
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const buckets = new Map<number, { r: number; g: number; b: number; n: number }>();

  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 200) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.n += 1;
    } else {
      buckets.set(key, { r, g, b, n: 1 });
    }
  }

  const candidates: Array<{
    rgb: [number, number, number];
    score: number;
    hsl: [number, number, number];
  }> = [];
  for (const bucket of buckets.values()) {
    const r = bucket.r / bucket.n;
    const g = bucket.g / bucket.n;
    const b = bucket.b / bucket.n;
    const hsl = rgbToHsl(r, g, b);
    const passLight =
      mode === "joyful" ? hsl[2] > 0.18 && hsl[2] < 0.88 : hsl[2] > 0.05 && hsl[2] < 0.6;
    if (!passLight) continue;
    // Score by saturation × frequency. Punish very low saturation slightly.
    const sat = hsl[1];
    const freq = bucket.n;
    const score = freq * (0.25 + sat * 1.5);
    candidates.push({ rgb: [r, g, b], score, hsl });
  }

  candidates.sort((a, b) => b.score - a.score);
  if (candidates.length === 0) return fallbackTheme(mode);

  // Primary: top scorer. Accent: best alternative hue (>40° away from primary).
  const primary = candidates[0];
  let accent = candidates[1] ?? primary;
  for (const c of candidates.slice(1, 12)) {
    const dh = Math.abs(c.hsl[0] - primary.hsl[0]);
    const hueDiff = Math.min(dh, 1 - dh);
    if (hueDiff > 0.12 && c.hsl[1] > 0.25) {
      accent = c;
      break;
    }
  }

  const swatches = candidates.slice(0, 5).map((c) => rgbToHex(c.rgb[0], c.rgb[1], c.rgb[2]));

  return {
    primary: rgbToHex(primary.rgb[0], primary.rgb[1], primary.rgb[2]),
    accent: rgbToHex(accent.rgb[0], accent.rgb[1], accent.rgb[2]),
    swatches,
    mode,
  };
}

function fallbackTheme(mode: ThemeMode): ExtractedTheme {
  if (mode === "mourning") {
    return {
      primary: "#2A1A1A",
      accent: "#7A4A3A",
      swatches: ["#2A1A1A", "#7A4A3A"],
      mode,
    };
  }
  return {
    primary: "#B7791F",
    accent: "#E8B14A",
    swatches: ["#B7791F", "#E8B14A"],
    mode,
  };
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
    }
    h /= 6;
  }
  return [h, s, l];
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}

export type BannerManifestFormat = "webp" | "jpeg" | "avif" | "png";

export type BannerManifest = {
  id: string;
  alt: string;
  src: string;
  variants: Array<{ url: string; width: number; height?: number; format: BannerManifestFormat }>;
};

const ALLOWED_MANIFEST_FORMATS: ReadonlySet<BannerManifestFormat> = new Set([
  "webp",
  "jpeg",
  "avif",
  "png",
]);

function isManifestFormat(value: unknown): value is BannerManifestFormat {
  return typeof value === "string" && ALLOWED_MANIFEST_FORMATS.has(value as BannerManifestFormat);
}

function parseManifest(value: unknown): BannerManifest {
  if (!value || typeof value !== "object") throw new Error("Malformed banner response.");
  const v = value as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.src !== "string" || !Array.isArray(v.variants)) {
    throw new Error("Malformed banner response.");
  }
  const variants = v.variants.map((entry, i) => {
    if (!entry || typeof entry !== "object") {
      throw new Error(`Malformed variant at ${i}.`);
    }
    const row = entry as Record<string, unknown>;
    if (
      typeof row.url !== "string" ||
      typeof row.width !== "number" ||
      !isManifestFormat(row.format)
    ) {
      throw new Error(`Invalid variant at ${i}.`);
    }
    const heightRaw = row.height;
    const height = typeof heightRaw === "number" && heightRaw > 0 ? heightRaw : undefined;
    return { url: row.url, width: row.width, height, format: row.format };
  });
  return {
    id: v.id,
    alt: typeof v.alt === "string" ? v.alt : "",
    src: v.src,
    variants,
  };
}

export async function uploadBanner(slug: string, pipe: PipelineOutput): Promise<BannerManifest> {
  const form = new FormData();
  form.append("slug", slug);
  form.append("id", pipe.id);
  form.append("alt", pipe.alt);
  for (const variant of pipe.variants) {
    form.append("variant", variant.blob, variant.filename);
    form.append("variantW", String(variant.width));
    form.append("variantH", String(variant.height));
  }
  const res = await fetch("/api/uploads/banner", {
    method: "POST",
    body: form,
    credentials: "same-origin",
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`Upload failed: ${res.status} ${msg}`);
  }
  return parseManifest(await res.json());
}
